import React, { useState } from "react";
import { Button, Input, Typography, Tooltip } from "antd";
import {
  FrownOutlined,
  MehOutlined,
  SmileOutlined,
  StarOutlined,
  HeartOutlined,
} from "@ant-design/icons";
const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const RateYourDine = ({ reservation, config, onBack }) => {
  const [ratings, setRatings] = useState({
    foodQuality: null,
    serviceQuality: null,
    ambiance: null,
    valueForMoney: null,
    overallExperience: null,
  });
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleRatingChange = (category, value) => {
    setRatings((prev) => ({ ...prev, [category]: value }));
  };

  const handleSubmit = () => {
    if (Object.values(ratings).some((rating) => rating === null)) {
      alert("Please provide a rating for all categories before submitting.");
      return;
    }
    console.log("Submitting feedback:", {
      reservationId: reservation.id,
      ratings,
      feedback,
    });
    setSubmitted(true);
  };

  const ratingOptions = [
    { value: 1, label: "Terrible", icon: <FrownOutlined />, color: "#ef4444" },
    { value: 2, label: "Okay", icon: <MehOutlined />, color: "#f59e0b" },
    { value: 3, label: "Good", icon: <SmileOutlined />, color: "#3b82f6" },
    { value: 4, label: "Great", icon: <StarOutlined />, color: "#10b981" },
    { value: 5, label: "Amazing", icon: <HeartOutlined />, color: "#d946ef" },
  ];

  const feedbackCategories = [
    { key: "foodQuality", label: "Food Quality" },
    { key: "serviceQuality", label: "Service Quality" },
    { key: "ambiance", label: "Ambiance" },
    { key: "valueForMoney", label: "Value for Money" },
    { key: "overallExperience", label: "Overall Experience" },
  ];

  return (
    <div className="rate-your-dine">
      {!submitted ? (
        <>
          <Title level={2} className="title" style={{ color: config.colors.primary }}>
            Rate Your Dining Experience
          </Title>
          <Paragraph className="subtitle">
            How was your visit on{" "}
            <strong>
              {new Date(reservation.start).toLocaleDateString()} at{" "}
              {new Date(reservation.start).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </strong>
            ?
          </Paragraph>
          {/* Rating Categories */}
          {feedbackCategories.map((category) => (
            <div key={category.key} className="rating-section">
              <div className="rating-row">
                <span className="category-label">{category.label}</span>
                <div className="rating-options">
                  {ratingOptions.map((opt) => (
                    <Tooltip key={opt.value} title={opt.label}>
                      <span
                        className={`rating-icon ${ratings[category.key] === opt.value ? "selected" : ""}`}
                        style={{ color: ratings[category.key] === opt.value ? opt.color : "#d1d5db" }}
                        onClick={() => handleRatingChange(category.key, opt.value)}
                      >
                        {opt.icon}
                      </span>
                    </Tooltip>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {/* Feedback Textarea */}
          <TextArea
            rows={4}
            placeholder="Share your thoughts or suggestions..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="feedback-box"
          />
          {/* Actions */}
          <div className="actions">
            <Button onClick={onBack}>Back</Button>
            <Button
              type="primary"
              style={{ background: config.colors.primary, borderColor: config.colors.primary }}
              onClick={handleSubmit}
            >
              Submit Feedback
            </Button>
          </div>
        </>
      ) : (
        <div className="thank-you">
          <Title level={2} style={{ color: config.colors.primary }}>
            🎉 Thank You!
          </Title>
          <Paragraph>
            Your feedback has been submitted. We appreciate you helping us improve{" "}
            {config.company_name || "our restaurant"}!
          </Paragraph>
          <Button
            type="primary"
            style={{ background: config.colors.primary, borderColor: config.colors.primary }}
            onClick={onBack}
          >
            Back to Dashboard
          </Button>
        </div>
      )}
      <style>{`
        .rate-your-dine {
          max-width: 600px;
          margin: 0 auto;
          padding: 24px;
          font-family: ${config.font || "Arial, sans-serif"};
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }
        .title {
          text-align: center;
          margin-bottom: 16px;
          font-weight: 700;
        }
        .subtitle {
          text-align: center;
          color: #6b7280;
          margin-bottom: 32px;
          font-size: 14px;
        }
        .rating-section {
          margin-bottom: 24px;
        }
        .rating-row {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .category-label {
          color: #374151;
          font-weight: 600;
          font-size: 16px;
          min-width: 120px;
        }
        .rating-options {
          display: flex;
          gap: 12px;
        }
        .rating-icon {
          font-size: 24px;
          cursor: pointer;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .rating-icon:hover {
          transform: scale(1.1);
        }
        .rating-icon.selected {
          color: ${config.colors.primary} !important;
        }
        .feedback-box {
          margin-bottom: 24px;
          border-radius: 8px;
          border-color: #e5e7eb;
          font-size: 14px;
          width: 100%;
        }
        .feedback-box:focus {
          border-color: ${config.colors.primary};
          box-shadow: 0 0 0 2px rgba(${config.colors.primary.replace("#", "").slice(0, -2)}, 0.2);
        }
        .actions {
          display: flex;
          justify-content: space-between;
          margin-top: 24px;
        }
        .thank-you {
          text-align: center;
          padding: 32px;
        }
        @media (max-width: 480px) {
          .rate-your-dine {
            padding: 16px;
          }
          .title {
            font-size: 20px;
          }
          .subtitle {
            font-size: 12px;
          }
          .rating-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          .category-label {
            font-size: 14px;
          }
          .rating-options {
            gap: 8px;
          }
          .rating-icon {
            font-size: 20px;
          }
          .feedback-box {
            font-size: 12px;
          }
          .actions {
            flex-direction: column;
            gap: 12px;
          }
          .actions button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default RateYourDine;